// networkMetrics.ts
export type NetworkMetrics = {
  timestampISO: string;

  // Básicos (universais)
  online: boolean | '';
  visibilityState: DocumentVisibilityState | '';
  language: string | '';
  platform: string | '';
  userAgent: string | '';

  // Network Information API (quando existe)
  connection_type: string | '';           // conn.type (raramente existe)
  connection_effectiveType: string | '';  // 4g/3g/2g/slow-2g
  connection_downlinkMbps: number | '';
  connection_rttMs: number | '';
  connection_saveData: boolean | '';
  connection_downlinkMaxMbps: number | ''; // alguns browsers expõem

  // Navigation Timing (quando existe)
  nav_transferSize: number | '';
  nav_encodedBodySize: number | '';
  nav_decodedBodySize: number | '';
  nav_domCompleteMs: number | ''; // duração aproximada do carregamento

  // Ping (medição real)
  ping_url: string | '';
  ping_latencyMs: number | '';
  ping_ok: boolean | '';
  ping_error: string | '';
};

function toNum(v: any): number | '' {
  return typeof v === 'number' && Number.isFinite(v) ? v : '';
}
function toBool(v: any): boolean | '' {
  return typeof v === 'boolean' ? v : '';
}
function toStr(v: any): string | '' {
  return typeof v === 'string' ? v : '';
}

async function measurePing(url: string, timeoutMs = 4000) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const id = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  const start = typeof performance !== 'undefined' ? performance.now() : Date.now();

  try {
    const res = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      signal: controller?.signal,
      // mode: 'cors', // se for outro domínio, você pode precisar ajustar
    });

    const end = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const latency = end - start;

    return {
      ok: true,
      latencyMs: latency,
      error: '',
      status: res.status,
    };
  } catch (e: any) {
    const end = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const latency = end - start;

    return {
      ok: false,
      latencyMs: latency,
      error: toStr(e?.name) || toStr(e?.message) || 'ping_failed',
      status: '',
    };
  } finally {
    if (id) clearTimeout(id);
  }
}

function getNavTiming() {
  try {
    if (typeof performance === 'undefined' || !performance.getEntriesByType) return null;
    const nav = performance.getEntriesByType('navigation')?.[0] as any;
    if (!nav) return null;

    // domComplete - startTime dá a duração total aproximada do load
    const domCompleteMs =
      toNum(nav.domComplete) !== '' && toNum(nav.startTime) !== ''
        ? (nav.domComplete - nav.startTime)
        : '';

    return {
      transferSize: toNum(nav.transferSize),
      encodedBodySize: toNum(nav.encodedBodySize),
      decodedBodySize: toNum(nav.decodedBodySize),
      domCompleteMs: toNum(domCompleteMs),
    };
  } catch {
    return null;
  }
}

function getConnectionInfo() {
  const navAny = navigator as any;
  const conn = navAny?.connection || navAny?.mozConnection || navAny?.webkitConnection;

  if (!conn) return null;

  return {
    type: toStr(conn.type),
    effectiveType: toStr(conn.effectiveType),
    downlink: toNum(conn.downlink),
    downlinkMax: toNum(conn.downlinkMax),
    rtt: toNum(conn.rtt),
    saveData: toBool(conn.saveData),
  };
}

/**
 * Coleta o máximo possível, preenchendo '' quando não existir.
 * @param pingUrl exemplo: '/ping' ou 'https://api.seudominio.com/ping'
 */
export async function collectNetworkMetrics(pingUrl: string = '/ping'): Promise<NetworkMetrics> {
  const connInfo = getConnectionInfo();
  const navTiming = getNavTiming();

  // básicos
  const online = typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
    ? navigator.onLine
    : '';

  const visibilityState =
    typeof document !== 'undefined' ? (document.visibilityState || '') : '';

  const language =
    typeof navigator !== 'undefined' ? (navigator.language || '') : '';

  const platform =
    typeof navigator !== 'undefined' ? ((navigator as any).platform || '') : '';

  const userAgent =
    typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';

  // ping real
  let ping_ok: boolean | '' = '';
  let ping_latencyMs: number | '' = '';
  let ping_error: string | '' = '';
  let ping_url: string | '' = pingUrl || '';

  if (pingUrl) {
    const ping = await measurePing(pingUrl);
    ping_ok = ping.ok;
    ping_latencyMs = toNum(ping.latencyMs);
    ping_error = ping.ok ? '' : ping.error;
  }

  return {
    timestampISO: new Date().toISOString(),

    online,
    visibilityState: (visibilityState as any) || '',
    language,
    platform,
    userAgent,

    connection_type: connInfo?.type ?? '',
    connection_effectiveType: connInfo?.effectiveType ?? '',
    connection_downlinkMbps: connInfo?.downlink ?? '',
    connection_rttMs: connInfo?.rtt ?? '',
    connection_saveData: connInfo?.saveData ?? '',
    connection_downlinkMaxMbps: connInfo?.downlinkMax ?? '',

    nav_transferSize: navTiming?.transferSize ?? '',
    nav_encodedBodySize: navTiming?.encodedBodySize ?? '',
    nav_decodedBodySize: navTiming?.decodedBodySize ?? '',
    nav_domCompleteMs: navTiming?.domCompleteMs ?? '',

    ping_url,
    ping_latencyMs,
    ping_ok,
    ping_error,
  };
}
